import { useEffect, useRef, useCallback, useState } from 'react';
import { Socket } from 'socket.io-client';
import { getSocket, disconnectSocket } from '../services/socket';

type SocketEventHandler = (...args: unknown[]) => void;
type SocketEventMap = Record<string, SocketEventHandler>;

interface UseSocketOptions {
  enabled?: boolean;
  events?: SocketEventMap;
}

export const useSocket = ({ enabled = true, events = {} }: UseSocketOptions = {}) => {
  const socketRef = useRef<Socket | null>(null);
  const eventHandlersRef = useRef<Map<string, SocketEventHandler>>(new Map());
  const [currentSocket, setCurrentSocket] = useState<Socket | null>(null);

  const emit = useCallback((event: string, ...args: unknown[]) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, ...args);
    } else if (import.meta.env.DEV) {
      console.warn(`Socket not connected, cannot emit event: ${event}`);
    }
  }, []);

  const on = useCallback((event: string, handler: SocketEventHandler) => {
    if (socketRef.current) {
      const oldHandler = eventHandlersRef.current.get(event);
      if (oldHandler) {
        socketRef.current.off(event, oldHandler);
      }

      socketRef.current.on(event, handler);
      eventHandlersRef.current.set(event, handler);
    }
  }, []);

  const off = useCallback((event: string) => {
    if (socketRef.current) {
      const handler = eventHandlersRef.current.get(event);
      if (handler) {
        socketRef.current.off(event, handler);
        eventHandlersRef.current.delete(event);
      }
    }
  }, []);

  useEffect(() => {
    const handlerStore = eventHandlersRef.current;

    if (!enabled) {
      const activeSocket = socketRef.current;
      if (activeSocket) {
        handlerStore.forEach((handler, event) => {
          activeSocket.off(event, handler);
        });
      }
      handlerStore.clear();
      socketRef.current = null;
      setCurrentSocket(null);
      return;
    }

    let mounted = true;

    const initSocket = async () => {
      try {
        const socket = await getSocket();
        if (!mounted) {
          return;
        }

        socketRef.current = socket;
        setCurrentSocket(socket ?? null);

        if (!socket && import.meta.env.DEV) {
          console.log('[WS] Socket disabled or unavailable');
        }
      } catch (error) {
        console.error('Failed to initialize socket:', error);
      }
    };

    initSocket();

    return () => {
      mounted = false;
      const activeSocket = socketRef.current;
      const handlersSnapshot = new Map(handlerStore);
      handlerStore.clear();

      if (activeSocket) {
        handlersSnapshot.forEach((handler, event) => {
          activeSocket.off(event, handler);
        });
      }

      socketRef.current = null;
      setCurrentSocket(null);
    };
  }, [enabled]);

  useEffect(() => {
    const socket = currentSocket;
    if (!socket) {
      return;
    }

    const handlerStore = eventHandlersRef.current;

    handlerStore.forEach((handler, event) => {
      if (!events[event]) {
        socket.off(event, handler);
        handlerStore.delete(event);
      }
    });

    Object.entries(events).forEach(([event, handler]) => {
      if (typeof handler !== 'function') {
        return;
      }

      const storedHandler = handlerStore.get(event);
      if (storedHandler !== handler) {
        if (storedHandler) {
          socket.off(event, storedHandler);
        }
        socket.on(event, handler);
        handlerStore.set(event, handler);
      }
    });
  }, [events, currentSocket]);

  return {
    socket: currentSocket,
    connected: currentSocket?.connected || false,
    emit,
    on,
    off,
  };
};

// Hook for cleaning up socket on logout
export const useSocketCleanup = () => {
  useEffect(() => {
    return () => {
      // Disconnect socket when component using this hook unmounts
      disconnectSocket();
    };
  }, []);
};
